import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import liff from "../../../lib/liff";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import {
  getMemoryRollbackStatus,
  getUserEpisode,
  scheduleModeSwitch,
  unscheduleModeSwitch,
} from "utils/api";
import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";
import EpisodeStateCard from "works/chi/components/EpisodeStateCard";
import StyledButton from "works/chi/components/StyledButton";

import { COLORS } from "constants/colors";
import AppLayout from "works/chi/components/AppLayout";
import EpisodeError from "works/chi/components/EpisodeError";
import EpisodeEmpty from "works/chi/components/EpisodeEmpty";
import { modeDescriptionUrls } from "constants/links";

const sectionTitleSx = {
  fontFamily: '"Noto Serif JP", serif',
  fontSize: "14px",
  fontWeight: "bold",
  color: COLORS.TEXT_WHITE,
  lineHeight: 1,
  px: 2,
  py: 0.75,
  backgroundColor: "rgba(32, 46, 70, 0.86)",
  width: "100%",
  textAlign: "center",
};

const actionSectionSx = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  mt: 4,
};

const MyPageSection = ({ title, children }) => (
  <Box sx={actionSectionSx}>
    <Typography sx={sectionTitleSx}>{title}</Typography>
    {children}
  </Box>
);

const sectionButtonSx = {
  mt: 3,
};

const processingSectionButtonSx = {
  mt: 3,
  background: "white",
};

function MyPage() {
  const { t, i18n } = useTranslation(["chi"]);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // クエリパラメータを取得
  const soulId = queryParams.get("soul_id");
  const lng = queryParams.get("lng") || "ja";

  const [userEpisode, setUserEpisode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  /* リセット機能 */
  const [openResetConfirm, setResetOpenConfirm] = useState(false);
  const [openResetNotice, setResetOpenNotice] = useState(false);
  const [openComplete, setOpenComplete] = useState(false);
  const [openResetError, setResetOpenError] = useState(false);

  /* モード切り替え予約 */
  const [openScheduleModeSwitchConfirm, setOpenScheduleModeSwitchConfirm] =
    useState(false);
  const [openScheduleModeSwitchComplete, setOpenScheduleModeSwitchComplete] =
    useState(false);
  const [openScheduleModeSwitchError, setOpenScheduleModeSwitchError] =
    useState(false);
  const [canScheduleModeSwitch, setCanScheduleModeSwitch] = useState(false); // モード切り替えが可能かどうか
  const [isScheduled, setIsScheduled] = useState(false); // モード切り替えが予約されているかどうか

  const [scheduleModeSwitchDescription, setScheduleModeSwitchDescription] =
    useState(""); // モード切り替えの説明文
  const [unscheduleModeSwitchDescription, setUnscheduleModeSwitchDescription] =
    useState(""); // モード切り替え削除の説明文
  const [
    openScheduleModeSwitchUnavailable,
    setOpenScheduleModeSwitchUnavailable,
  ] = useState(false);

  /* モード切り替え解除 */
  const [openUnscheduleModeSwitchConfirm, setOpenUnscheduleModeSwitchConfirm] =
    useState(false);
  const [
    openUnscheduleModeSwitchComplete,
    setOpenUnscheduleModeSwitchComplete,
  ] = useState(false);
  const [openUnscheduleModeSwitchError, setOpenUnscheduleModeSwitchError] =
    useState(false);

  /* その他 */
  const [afterPurchase, setAfterPurchase] = useState(true);
  const [isError, setIsError] = useState(false);
  const [hasResetOptions, setHasResetOptions] = useState(false);
  const [canSoftReset, setCanSoftReset] = useState(false);
  const [openMemoryRollbackCooldown, setOpenMemoryRollbackCooldown] =
    useState(false);
  const [openMemoryRollbackError, setOpenMemoryRollbackError] = useState(false);
  const [memoryRollbackCooldownUntil, setMemoryRollbackCooldownUntil] =
    useState(null);

  const getSoulName = (soulId) => {
    return soulId.split("_")[0];
  };

  // ルートに ?lng=xx をつける
  const withLng = (path) => {
    const hasQuery = path.includes("?");
    return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
  };

  const handleNavigateEmailSettings = async () => {
    navigate(withLng(`/chi/email-settings?soul_id=${soulId}`));
  };

  const handleResetConfirmClose = () => {
    setResetOpenConfirm(false);
  };

  const handleResetNoticeClose = () => {
    setResetOpenNotice(false);
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleRequestForm = () => {
    navigate(withLng(`/chi/request-form?soul_id=${soulId}`));
  };

  /* モード切り替え */
  const handleClickModeScheduleSwitch = async () => {
    if (!canScheduleModeSwitch && isScheduled) {
      setOpenUnscheduleModeSwitchConfirm(true);
      return;
    }

    if (canScheduleModeSwitch) {
      setOpenScheduleModeSwitchConfirm(true);
      return;
    }

    if (!canScheduleModeSwitch) {
      setOpenScheduleModeSwitchUnavailable(true);
      return;
    }
  };

  const handleClickScheduleModeSwitchConfirm = async () => {
    try {
      setIsProcessing(true);
      await scheduleModeSwitch(soulId);
      setOpenScheduleModeSwitchComplete(true);
      setCanScheduleModeSwitch(false);
      setIsScheduled(true);
    } catch (error) {
      setOpenScheduleModeSwitchError(true);
    } finally {
      setOpenScheduleModeSwitchConfirm(false);
      setIsProcessing(false);
    }
  };
  const handleClickUnscheduleModeSwitchConfirm = async () => {
    try {
      setIsProcessing(true);
      await unscheduleModeSwitch(soulId);
      setOpenUnscheduleModeSwitchComplete(true);
      setCanScheduleModeSwitch(true);
      setIsScheduled(false);
    } catch (error) {
      setOpenUnscheduleModeSwitchError(true);
    } finally {
      setOpenUnscheduleModeSwitchConfirm(false);
      setIsProcessing(false);
    }
  };

  /* 記憶リセット */
  const handleResetClickConfirm = async (resetType = "hard") => {
    try {
      setResetOpenConfirm(false);
      setIsProcessing(true);
      const messageText = resetType === "soft" ? "**sreset**" : "**reset**";
      liff.sendMessages([
        {
          type: "text",
          text: messageText,
        },
      ]);
      liff.closeWindow();
      setOpenComplete(true);
    } catch (error) {
      setResetOpenError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSoftResetClickConfirm = async () => {
    await handleResetClickConfirm("soft");
  };

  const handleResetCompleteClose = () => {
    setOpenComplete(false);
  };

  const handleResetErrorClose = () => {
    setResetOpenError(false);
  };

  const onPromote = () => {
    navigate(withLng(`/chi/tickets?soul_id=${soulId}`));
  };

  const handleMemoryRollback = async () => {
    try {
      setIsProcessing(true);
      const status = await getMemoryRollbackStatus(soulId);
      if (!status.can_request) {
        setMemoryRollbackCooldownUntil(status.cooldown_until);
        setOpenMemoryRollbackCooldown(true);
        return;
      }
      navigate(withLng(`/chi/memory-rollback?soul_id=${soulId}`));
    } catch (error) {
      setOpenMemoryRollbackError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuestClearRequest = () => {
    navigate(withLng(`/chi/quest-clear-request?soul_id=${soulId}`));
  };

  const onClickResetMemory = () => {
    if (userEpisode.can_reset) {
      setResetOpenConfirm(true);
    } else {
      setResetOpenNotice(true);
    }
  };

  const formatDateTime = (isoString, lng) => {
    const zone = lng === "en" ? "Europe/Warsaw" : "Asia/Tokyo"; // 言語に応じてタイムゾーンを設定
    const dt = DateTime.fromISO(isoString, { zone: "utc" }) // UTCとして解釈
      .setZone(zone)
      .setLocale(lng);

    if (lng === "en") {
      return dt.toFormat("MMM d, yyyy HH:mm"); // 例: May 3, 2024 18:41
    } else {
      return dt.toFormat("yyyy/MM/dd HH:mm"); // 例: 2024/05/03 18:41
    }
  };

  const createScheduleModeSwitchDescription = (modeInfo) => {
    const mode_after_switch = t(`chi:mode.${modeInfo.mode_after_switch}`);
    const remaining_days = modeInfo.remaining_days_after_switch;
    const switch_at_raw = modeInfo.switch_at;
    const switch_at = formatDateTime(switch_at_raw, lng);

    return t(`chi:button.description.schedule_mode_switch`, {
      mode_after_switch,
      remaining_days,
      switch_at,
    });
  };

  const createUnscheduleModeSwitchDescription = (modeInfo) => {
    const mode_after_switch = t(`chi:mode.${modeInfo.mode_after_switch}`);
    // const remaining_days = modeInfo.remaining_days_after_switch;
    const switch_at_raw = modeInfo.switch_at;
    const switch_at = formatDateTime(switch_at_raw, lng);

    return t(`chi:button.description.unschedule_mode_switch`, {
      mode_after_switch,
      switch_at,
    });
  };

  const initUserEpisode = async (soulId) => {
    try {
      const userEpisodeData = await getUserEpisode(soulId);

      setUserEpisode(userEpisodeData);

      if (userEpisodeData === null) {
        setAfterPurchase(false);
      }

      if (userEpisodeData !== null) {
        setCanScheduleModeSwitch(userEpisodeData.mode_info.can_schedule);
        setIsScheduled(userEpisodeData.mode_info.is_scheduled);
        setHasResetOptions(userEpisodeData.has_reset_options || false);
        setCanSoftReset(userEpisodeData.can_soft_reset || false);

        setScheduleModeSwitchDescription(
          createScheduleModeSwitchDescription(userEpisodeData.mode_info)
        );
        setUnscheduleModeSwitchDescription(
          createUnscheduleModeSwitchDescription(userEpisodeData.mode_info)
        );
      }
    } catch (error) {
      // 修正箇所: エピソードが購入されていない場合の処理を追加
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 初回ロード時
  useEffect(() => {
    initUserEpisode(soulId);
  }, []);

  return (
    <AppLayout>
      {isLoading && <Loading />}
      {!isLoading && isError && <EpisodeError />}
      {!isLoading && !isError && !afterPurchase && (
        <EpisodeEmpty onClick={onPromote} />
      )}
      {!isLoading && !isError && afterPurchase && (
        <Box
          sx={{
            display: "flex",
            paddingTop: 4, // 任意で上マージンを追加
            paddingBottom: 4, // 任意で下マージンを追加
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Noto Serif JP", serif',
                fontSize: "20px",
                fontWeight: "bold",
                color: COLORS.TEXT_WHITE,
              }}
            >
              Character
            </Typography>
          </Box>
          <EpisodeStateCard
            userEpisode={userEpisode}
            soulName={getSoulName(soulId)}
          />
          <MyPageSection title={t("chi:myPageSections.requests")}>
            <StyledButton
              variant="contained"
              disabled={isProcessing}
              sx={sectionButtonSx}
              onClick={handleMemoryRollback}
            >
              {t("common:button.memory_rollback")}
            </StyledButton>
            <StyledButton
              variant="contained"
              sx={sectionButtonSx}
              onClick={handleQuestClearRequest}
            >
              {t("chi:button.quest_clear_request")}
            </StyledButton>
            <StyledButton
              variant="contained"
              sx={sectionButtonSx}
              onClick={handleRequestForm}
            >
              {t("chi:button.request_form")}
            </StyledButton>
          </MyPageSection>

          <MyPageSection title={t("chi:myPageSections.operations")}>
            {userEpisode.is_expired && (
              <StyledButton
                variant="contained"
                sx={isProcessing ? processingSectionButtonSx : sectionButtonSx}
                onClick={onPromote}
              >
                {t("chi:button.purchase_episode")}
              </StyledButton>
            )}
            {!userEpisode.is_expired && (
              <StyledButton
                variant="contained"
                disabled={isProcessing}
                onClick={onClickResetMemory}
                sx={isProcessing ? processingSectionButtonSx : sectionButtonSx}
              >
                {t("chi:button.reset_memories")}
              </StyledButton>
            )}
            {!userEpisode.is_expired && (
              <StyledButton
                variant="contained"
                onClick={() => handleClickModeScheduleSwitch()}
                sx={isProcessing ? processingSectionButtonSx : sectionButtonSx}
              >
                {t("chi:button.schedule_mode_switch")}
              </StyledButton>
            )}
            <StyledButton
              variant="contained"
              sx={sectionButtonSx}
              onClick={handleNavigateEmailSettings}
            >
              {t("common:button.email_settings")}
            </StyledButton>
            <StyledButton
              variant="contained"
              sx={sectionButtonSx}
              onClick={handleCloseWindow}
            >
              {t("chi:button.back_to_chat")}
            </StyledButton>
          </MyPageSection>
        </Box>
      )}

      {/* モード切り替え予約 */}
      <ButtonDialog
        open={openScheduleModeSwitchConfirm}
        title={t("chi:button.dialog.schedule_mode_switch_confirm")}
        description={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <a
                href={modeDescriptionUrls[lng]}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: COLORS.SKY_BLUE,
                  textDecoration: "none",
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                {t("chi:button.description.what_is_mode")}
              </a>
            </div>
            <div style={{ textAlign: "center", marginBottom: 0 }}>
              {scheduleModeSwitchDescription}
            </div>
          </>
        }
        buttons={[
          {
            label: `${t("chi:button.schedule")}`,
            onClick: handleClickScheduleModeSwitchConfirm,
            disabled: isProcessing,
          },
        ]}
        handleClose={() => setOpenScheduleModeSwitchConfirm(false)}
      />
      <ButtonDialog
        open={openScheduleModeSwitchComplete}
        title={`${t("chi:button.dialog.schedule_mode_switch_success")}`}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: () => setOpenScheduleModeSwitchComplete(false),
          },
        ]}
        handleClose={() => setOpenScheduleModeSwitchComplete(false)}
      />
      <ButtonDialog
        open={openScheduleModeSwitchError}
        title={`${t("chi:button.dialog.schedule_mode_switch_fail")}`}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: () => setOpenScheduleModeSwitchError(false),
          },
        ]}
        handleClose={() => setOpenScheduleModeSwitchError(false)}
      />

      {/* モード切り替え予約削除 */}
      <ButtonDialog
        open={openUnscheduleModeSwitchConfirm}
        title={t("chi:button.dialog.unschedule_mode_switch_confirm")}
        description={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 12, // 下の説明文との余白
              }}
            >
              <a
                href={modeDescriptionUrls[lng]}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: COLORS.SKY_BLUE,
                  textDecoration: "none",
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                {t("chi:button.description.what_is_mode")}
              </a>
            </div>
            <div style={{ textAlign: "center", marginBottom: 0 }}>
              {unscheduleModeSwitchDescription}
            </div>
          </>
        }
        buttons={[
          {
            label: `${t("chi:button.unschedule")}`,
            onClick: handleClickUnscheduleModeSwitchConfirm,
            disabled: isProcessing,
          },
        ]}
        handleClose={() => setOpenUnscheduleModeSwitchConfirm(false)}
      />
      <ButtonDialog
        open={openUnscheduleModeSwitchComplete}
        title={`${t("chi:button.dialog.unschedule_mode_switch_success")}`}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: () => setOpenUnscheduleModeSwitchComplete(false),
          },
        ]}
        handleClose={() => setOpenUnscheduleModeSwitchComplete(false)}
      />
      <ButtonDialog
        open={openUnscheduleModeSwitchError}
        title={`${t("chi:button.dialog.unschedule_mode_switch_fail")}`}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: () => setOpenUnscheduleModeSwitchError(false),
          },
        ]}
        handleClose={() => setOpenUnscheduleModeSwitchError(false)}
      />

      {/* モード切り替え予約不可（事前制限） */}
      <ButtonDialog
        open={openScheduleModeSwitchUnavailable}
        title={`${t("chi:button.dialog.schedule_mode_switch_unavailable")}`}
        description={t(
          "chi:button.description.schedule_mode_switch_unavailable"
        )}
        notices={t("chi:button.notices.schedule_mode_switch_unavailable", {
          returnObjects: true,
        })}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: () => setOpenScheduleModeSwitchUnavailable(false),
          },
        ]}
        handleClose={() => setOpenScheduleModeSwitchUnavailable(false)}
      />

      {/* 記憶リセット */}
      <ButtonDialog
        open={openResetConfirm}
        title={
          hasResetOptions
            ? t("chi:button.dialog.reset_memories_with_options_confirm")
            : t("chi:button.dialog.reset_memories_confirm")
        }
        warnings={t("chi:button.warning.reset_memories", {
          returnObjects: true,
        })}
        buttons={[
          {
            label: hasResetOptions
              ? t("chi:button.hard_reset")
              : t("chi:button.reset"),
            onClick: () => handleResetClickConfirm("hard"),
          },
          ...(hasResetOptions
            ? [
              {
                label: t("chi:button.soft_reset"),
                onClick: handleSoftResetClickConfirm,
                disabled: !canSoftReset,
              },
            ]
            : []),
        ]}
        buttonNotice={
          hasResetOptions
            ? t("chi:button.dialog.reset_memories_with_options_notice")
            : ""
        }
        handleClose={handleResetConfirmClose}
      />
      <ButtonDialog
        open={openComplete}
        title={`${t("chi:button.dialog.reset_memories_success")}`}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: handleResetCompleteClose,
          },
        ]}
        handleClose={handleResetCompleteClose}
      />
      <ButtonDialog
        open={openResetNotice}
        title={t("chi:button.dialog.cannot_reset_memories")}
        handleClose={handleResetNoticeClose}
        buttons={[{ label: "閉じる", onClick: handleResetNoticeClose }]}
      />
      <ButtonDialog
        open={openResetError}
        // title={`記憶のリセットに失敗しました。もう一度お試しください。`}
        title={`${t("chi:button.dialog.reset_memories_fail")}`}
        buttons={[
          { label: `${t("chi:button.close")}`, onClick: handleResetErrorClose },
        ]}
        handleClose={handleResetErrorClose}
      />
      <ButtonDialog
        open={openMemoryRollbackCooldown}
        title={t("chi:memoryRollback.cooldownTitle")}
        description={t("chi:memoryRollback.cooldownDescription", {
          time: memoryRollbackCooldownUntil
            ? formatDateTime(memoryRollbackCooldownUntil, lng)
            : "",
        })}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: () => setOpenMemoryRollbackCooldown(false),
          },
        ]}
        handleClose={() => setOpenMemoryRollbackCooldown(false)}
      />
      <ButtonDialog
        open={openMemoryRollbackError}
        title={t("chi:memoryRollback.statusErrorTitle")}
        description={t("chi:memoryRollback.error")}
        buttons={[
          {
            label: `${t("chi:button.close")}`,
            onClick: () => setOpenMemoryRollbackError(false),
          },
        ]}
        handleClose={() => setOpenMemoryRollbackError(false)}
      />
    </AppLayout>
  );
}

export default MyPage;
